"use client"

import * as React from "react"
import { Clock } from "lucide-react"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { useTimerTranslations } from "@/hooks/use-translations"


interface TimePickerProps {
  date: Date | undefined
  setDate: (date: Date | undefined) => void
}

export function TimePickerDemo({ date, setDate }: TimePickerProps) {
  const t = useTimerTranslations()
  const minuteRef = React.useRef<HTMLInputElement>(null)
  const hourRef = React.useRef<HTMLInputElement>(null)

  const [hour, setHour] = React.useState<number>(date ? date.getHours() : new Date().getHours())
  const [minute, setMinute] = React.useState<number>(date ? date.getMinutes() : new Date().getMinutes())

  React.useEffect(() => {
    if (!date) return

    const nextHour = date.getHours()
    const nextMinute = date.getMinutes()

    setHour((currentHour) => (currentHour === nextHour ? currentHour : nextHour))
    setMinute((currentMinute) => (currentMinute === nextMinute ? currentMinute : nextMinute))
  }, [date])

  // Update the date when the hour or minute changes
  React.useEffect(() => {
    if (
      date &&
      date.getHours() === hour &&
      date.getMinutes() === minute &&
      date.getSeconds() === 0 &&
      date.getMilliseconds() === 0
    ) {
      return
    }

    const newDate = date ? new Date(date) : new Date()
    newDate.setHours(hour)
    newDate.setMinutes(minute)
    newDate.setSeconds(0)
    newDate.setMilliseconds(0)
    setDate(newDate)
  }, [date, hour, minute, setDate])

  // Handle arrow up and down keys
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    min: number,
    max: number,
  ) => {
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setValue((prev) => (prev === max ? min : prev + 1))
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      setValue((prev) => (prev === min ? max : prev - 1))
    }
  }

  // Handle input change
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    min: number,
    max: number,
  ) => {
    const value = Number.parseInt(e.target.value, 10)
    if (!isNaN(value)) {
      setValue(Math.min(Math.max(value, min), max))
    }
  }

  // Format the value to always be two digits
  const formatValue = (value: number) => value.toString().padStart(2, "0")

  return (
    <div className="flex items-end gap-2">
      <div className="grid gap-1 text-center">
        <Label htmlFor="hours" className="text-xs">
          {t('hours')}
        </Label>
        <Input
          ref={hourRef}
          id="hours"
          className="w-16 text-center"
          value={formatValue(hour)}
          onChange={(e) => handleInputChange(e, setHour, 0, 23)}
          onKeyDown={(e) => handleKeyDown(e, setHour, 0, 23)}
          onFocus={(e) => e.target.select()}
        />
      </div>
      <div className="text-xl">:</div>
      <div className="grid gap-1 text-center">
        <Label htmlFor="minutes" className="text-xs">
          {t('minutes')}
        </Label>
        <Input
          ref={minuteRef}
          id="minutes"
          className="w-16 text-center"
          value={formatValue(minute)}
          onChange={(e) => handleInputChange(e, setMinute, 0, 59)}
          onKeyDown={(e) => handleKeyDown(e, setMinute, 0, 59)}
          onFocus={(e) => e.target.select()}
        />
      </div>
      <div className="grid gap-1 place-self-center">
        <Clock className="h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  )
}
